import { getLeadAttributes, type GetLeadAttributesType } from '@repo/advanced-filters';
import {
  accountFields,
  getActivityAttributes,
  type GetActivityAttributesProps,
  type MailMergeOptionsType,
  mailMergeOwnerFields,
} from '@repo/crm-atoms';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { Typography } from 'antd';
import { triggeredActivityForMailMergeSelector } from 'automations/selectors/reactFlow';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

interface UseMailMergeFieldsProps {
  allowedDataTypes?: string[];
  groupTitleClassName?: string;
}

export default function useMailMergeFields({
  allowedDataTypes,
  groupTitleClassName,
}: UseMailMergeFieldsProps) {
  const triggeredActivityId = useSelector(triggeredActivityForMailMergeSelector);

  const {
    data,
    isLoading: attributesLoading,
    error,
  } = useGetGraphqlDataQuery<UseQueryResult<GetLeadAttributesType>>({ document: getLeadAttributes });

  const {
    data: activityAttributesData,
    isLoading: activityAttributesLoading,
    error: activityAttributesError,
  } = useGetGraphqlDataQuery<UseQueryResult<GetActivityAttributesProps>>({
    document: getActivityAttributes,
    variables: {
      activityId: triggeredActivityId,
    },
  }, {
    skip: !triggeredActivityId,
  });

  useEffect(() => {
    if (error || activityAttributesError) {
      message.error('Failed to Load Mail Merge Attributes');
    }
  }, [error, activityAttributesError]);

  const allowedLeadAttributes = useMemo(
    () => {
      if (!allowedDataTypes || !allowedDataTypes.length) return data?.leadAttributes;

      return data?.leadAttributes?.filter(({ dataType }) => allowedDataTypes.includes(dataType));
    },
    [data?.leadAttributes, allowedDataTypes],
  );

  const allowedOwnerFields = useMemo(
    () => {
      if (!allowedDataTypes || !allowedDataTypes.length) return mailMergeOwnerFields;

      return mailMergeOwnerFields.filter(({ dataType }) => allowedDataTypes.includes(dataType));
    },
    [allowedDataTypes],
  );

  const allowedActivityFields = useMemo(
    () => {
      if (!allowedDataTypes || !allowedDataTypes.length) return activityAttributesData?.activityAttribute;

      return activityAttributesData?.activityAttribute?.filter(({ dataType }) => allowedDataTypes.includes(dataType));
    },
    [allowedDataTypes, activityAttributesData?.activityAttribute],
  );

  const allowedAccountFields = useMemo(
    () => {
      if (!allowedDataTypes || !allowedDataTypes.length) return accountFields;

      return accountFields.filter(({ dataType }) => allowedDataTypes.includes(dataType));
    },
    [allowedDataTypes],
  );

  const options = useMemo(
    (): MailMergeOptionsType[] => [
      {
        label: (
          <Typography.Text
            className={groupTitleClassName}
            type="secondary"
          >
            Lead Attributes
          </Typography.Text>
        ),
        title: 'Lead Attributes',
        options: allowedLeadAttributes?.map(({ label, schemaName }) => ({
          label: `Lead:${label}`,
          value: `Lead:${schemaName}`,
        })) || [],
      },
      {
        label: (
          <Typography.Text className={groupTitleClassName} type="secondary">
            Owner Fields
          </Typography.Text>
        ),
        title: 'Owner Fields',
        options: allowedOwnerFields.map(({ label, value }) => ({
          label: `Owner:${label}`,
          value: `Owner:${value}`,
        })),
      },
      {
        label: (
          <Typography.Text
            className={groupTitleClassName}
            type="secondary"
          >
            Triggered Activity Fields
          </Typography.Text>
        ),
        title: 'Activity Fields',
        options: allowedActivityFields?.map(({ attributeName, schemaName }) => ({
          label: `Activity:${attributeName}`,
          value: `Activity:${schemaName}`,
        })) || [],
      },
      {
        label: (
          <Typography.Text
            className={groupTitleClassName}
            type="secondary"
          >
            Account Fields
          </Typography.Text>
        ),
        title: 'Account Fields',
        options: allowedAccountFields.map(({ label, value }) => ({
          label: `Account:${label}`,
          value: `Account:${value}`,
        })),
      },
    ],
    [allowedLeadAttributes, allowedOwnerFields, allowedAccountFields, allowedActivityFields, groupTitleClassName],
  );

  return {
    options,
    isLoading: attributesLoading || activityAttributesLoading,
    error,
  };
}
